import { Copy, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { money } from "../lib/api";

const Row = ({ label, value, id }) => (
  <div className="flex items-center justify-between gap-3 py-3 border-b border-white/15">
    <span className="text-[#c9c3dc] text-sm">{label}</span>
    <span className="flex items-center gap-2">
      <span data-testid={`bank-${id}`} className="font-mono-x font-bold text-right break-all">{value || "-"}</span>
      <button data-testid={`copy-${id}`} aria-label={`Copy ${label}`} className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
        onClick={() => { navigator.clipboard?.writeText(String(value ?? "")); toast.success(`${label} copied`); }}>
        <Copy className="w-4 h-4" />
      </button>
    </span>
  </div>
);

const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });

const Schedule = ({ order }) => (
  <div className="rounded-[28px] chunk bg-white p-6 sm:p-8 mt-8" data-testid="plan-schedule">
    <h2 className="font-display font-extrabold text-2xl">Your payment plan</h2>
    <ol className="mt-4 divide-y-2 divide-dashed divide-[var(--ink)]/15">
      {(order?.schedule ?? []).map((s) => (
        <li key={s.n} className="flex justify-between gap-3 py-3" data-testid={`plan-payment-${s.n}`}>
          <span><strong>Payment {s.n}</strong> <span className="text-[#5a5470]">· due {s.n === 1 ? "now" : fmtDate(s.due)}</span></span>
          <span className="font-mono-x font-bold">{money(s.amount)}</span>
        </li>
      ))}
    </ol>
    <p className="mt-3 text-sm text-[#3b3550]">Total {money(order?.total)}. Your items are printed once the full amount has been paid.</p>
  </div>
);

export const BankDetails = ({ order, bank, shipping }) => (
  <main className="px-4 sm:px-8 pt-28 sm:pt-36 pb-24 min-h-screen layer-lines" data-testid="order-confirmation">
    <div className="max-w-3xl mx-auto">
      <div className="rounded-[28px] chunk bg-[var(--lime)] p-6 sm:p-10">
        <PartyPopper className="w-12 h-12" />
        <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl mt-4">Order placed!</h1>
        <p className="mt-3 text-lg">Thanks {order?.name}! Your order reference is <span data-testid="order-reference" className="font-mono-x font-bold bg-white px-2 py-0.5 rounded-lg border-2 border-[var(--ink)]">{order?.reference}</span></p>
        <p className="mt-3" data-testid="order-delivery">
          {order?.shipping_method === "pickup"
            ? `Pickup from ${shipping?.pickup?.detail ?? "our place"}. We'll email you to arrange a time.`
            : `Courier to: ${order?.address ?? ""}`}
        </p>
      </div>
      <div className="rounded-[28px] chunk bg-[var(--ink)] text-[var(--paper)] p-6 sm:p-10 mt-8" data-testid="bank-details-card">
        <h2 className="font-display font-extrabold text-2xl">Pay by bank transfer</h2>
        <p className="text-[#c9c3dc] mt-2 mb-4">
          {order?.installments > 1
            ? "Please transfer your first payment now and use your order reference as the payment reference. Your items are printed once the full amount has been paid."
            : "Please transfer the exact total and use your order reference as the payment reference. We'll start printing once payment arrives."}
        </p>
        <Row label={order?.installments > 1 ? "Pay now" : "Amount"} id="amount"
          value={money(order?.installments > 1 ? order?.schedule?.[0]?.amount : order?.total, bank?.currency || "NZD")} />
        <Row label="Reference" id="reference" value={order?.reference} />
        <Row label="Account name" id="account-name" value={bank?.account_name} />
        <Row label="Account number" id="account-number" value={bank?.account_number} />
        <Row label="Bank" id="bank-name" value={bank?.bank_name} />
      </div>
      {order?.installments > 1 && <Schedule order={order} />}
      <p className="mt-8 text-[#3b3550]">We've also emailed these details to {order?.email}. Questions? Email <a href="mailto:scratchs3dprintz@gmail.com" className="underline">scratchs3dprintz@gmail.com</a></p>
      <Link to="/" data-testid="order-back-home" className="inline-block mt-6 press chunk-sm rounded-full bg-[var(--yellow)] px-6 py-3 font-bold">Back to the shop</Link>
    </div>
  </main>
);
